"use client";

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Pagination,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { apiRemote, ISchool } from "@/api";

interface School {
  id: number;
  name: string;
  address: string;
}

const mockSchools: School[] = [
  { id: 1, name: "THCS Hoang Liet", address: "THCS Hoang Liet" },
  { id: 2, name: "Doan Thi Diem", address: "HCM" },
  {
    id: 3,
    name: "VN- Doan Thi Diem Secondary School 2",
    address: "Hà Nội, Saluysoy St. Mary'S College Of Meycauayan Bulacan",
  },
  { id: 4, name: "Doan Thi Diem Secondary School", address: "Hà Nội" },
  { id: 5, name: "Vinschool 4-1", address: "THCS Test 2 Ha Noi" },
  { id: 6, name: "Vinschool 5", address: "Hà Nội" },
  { id: 7, name: "THPT Ngo Thi Nham 5", address: "Hà Nội" },
  { id: 8, name: "THPT Ngo Thi Nham 1", address: "Hà Nội" },
  { id: 9, name: "THPT Viet Nam Ba Lan", address: "Hà Nội" },
  { id: 10, name: "Vinschool 3", address: "Hà Nội" },
];

interface SchoolSearchDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  state: string;
  onSchoolSelect: (id: number, name: string) => void;
}

export default function SchoolSearchDialog({
  open,
  setOpen,
  state,
  onSchoolSelect,
}: SchoolSearchDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [schools, setSchools] = useState<ISchool["data"]>([]);
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setPage(1);
  };

  const fetchSchools = async () => {
    setLoading(true);
    try {
      const res = await apiRemote.getSchoolByCity(
        page.toString(),
        state,
        searchTerm
      );
      if (res.success) {
        setSchools(res.data);
        setTotalPages(Math.ceil(res.count / itemsPerPage));
      } else {
        setSchools([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching schools:", error);
      setSchools([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1); // Reset to first page when performing a search
    fetchSchools();
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  useEffect(() => {
    if (open) {
      fetchSchools();
    }
  }, [open, page]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        Find your school
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSearch} sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Current City/State/Province: {state}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter school name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button
              variant="contained"
              type="submit"
              sx={{
                bgcolor: "#ff4d00",
                "&:hover": {
                  bgcolor: "#cc3d00",
                },
              }}
            >
              SEARCH
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell padding="checkbox" />
                <TableCell>School Name</TableCell>
                <TableCell>Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : schools.length > 0 ? (
                schools.map((school) => (
                  <TableRow key={school.id} hover>
                    <TableCell padding="checkbox">
                      <Button
                        size="small"
                        onClick={() => {
                          onSchoolSelect(school.id, school.SCHOOL_NAME);
                          setOpen(false); // Đóng dialog sau khi chọn
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                    <TableCell>{school.SCHOOL_NAME}</TableCell>
                    <TableCell>{school.ADDRESS}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No schools found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            mt: 2,
          }}
        >
          <Typography variant="body2">
            Page {page} of {totalPages}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            size="small"
            showFirstButton
            showLastButton
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button onClick={handleClose}>Close</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
